import { isUndefined } from 'lodash';
import { Graphics, Sprite, Text, TextStyle, Texture, Renderer, Assets, Container } from 'pixi.js';

import CTAItem from 'entity/CTAItem';
import ButtonSize from 'enums/ButtonSize';

import { COLOR_WHITE, FONT_FAMILY_MEDIUM, COLOR_BLACK, COLOR_GREY } from '../../constants';
import { ZIndex } from '../constants';
import { AppModuleServices } from 'app/utils/app-module-util';
import { LogService } from 'app/services/logging.service';

export const BUTTON_HEIGHT = 40;
export const BUTTON_WIDTH = 200;

export default class CTARoomItem extends Container {
  private mouseover?: () => void;
  private mouseout?: () => void;
  private pointerdown?: () => void;

  public buttonSprite?: Sprite;
  private buttonText?: Text;

  private imageTexture: Texture = Texture.EMPTY;
  public imageSprite?: Sprite;

  public itemID?: number;
  private renderer: Renderer;
  private logger: LogService;

  public constructor(
    renderer: Renderer,
    {
      compressed,
      item,
      mouseout,
      mouseover,
      pointerdown,
      ratio,
    }: {
      item: CTAItem;
      ratio: number;
      pointerdown: () => void;
      mouseover?: () => void;
      mouseout?: () => void;
      compressed: boolean;
    },
  ) {
    super();

    this.logger = AppModuleServices.injector.get<LogService>(LogService);
    this.x = item.x ?? 0;
    this.y = item.y ?? 0;
    this.zIndex = item.traversable ? ZIndex.Artifact : ZIndex.CTA;
    this.name = `cta-container-${item.name}`;
    this.mouseout = mouseout;
    this.mouseover = mouseover;
    this.pointerdown = pointerdown;
    this.renderer = renderer;

    if (item.imageURI) {
      void this.load(item, ratio, compressed);
    }

    if (item.buttonVisible) {
      this.createButton(item, ratio);
    }
  }

  private createButton(item: CTAItem, ratio: number): void {
    this.itemID = item.CTAItemId;
    const hasImage = !!item.imageURI;

    // Get ButtonSizeInfo based on item's buttonSize or default to MEDIUM if not specified
    const buttonSize = item.buttonSize !== undefined ? item.buttonSize : ButtonSize.MEDIUM;
    const buttonSizeInfo = this.buttonSizeExample.get(buttonSize) || this.buttonSizeExample.get(ButtonSize.MEDIUM)!;

    const buttonY = item.imageURI ? generateButtonY(this.y, item.imageHeight ?? 0, buttonSizeInfo.height) : this.y;

    const buttonContainer = new Container();
    buttonContainer.name = `cta-button-container-${item.CTAItemId}`;
    buttonContainer.eventMode = 'static';
    buttonContainer.cursor = 'pointer';

    const text = item.text ?? '';
    this.buttonText = createButtonText(text, ratio, item.buttonTextColor, buttonSizeInfo.fontSize);
    this.buttonText.anchor.set(0.5, 0.5);

    const buttonTexture = generateButtonTexture(
      this.renderer,
      ratio,
      item.buttonColor,
      buttonSizeInfo.width,
      buttonSizeInfo.height,
    );

    this.buttonSprite = Sprite.from(buttonTexture);
    this.buttonSprite.name = `button-${item.CTAItemId}`;
    this.buttonSprite.height = buttonSizeInfo.height * ratio;
    this.buttonSprite.width = buttonSizeInfo.width * ratio;
    this.buttonSprite.anchor.set(0.5, 0.5);
    this.buttonSprite.visible = true;
    if (hasImage) {
      this.buttonText.y = buttonY;
      this.buttonSprite.y = buttonY;
    }

    buttonContainer
      .on('pointerdown', () => this.pointerdown?.())
      .on('pointerenter', () => {
        this.mouseover?.();
        if (this.buttonText) {
          this.buttonText.style.fill = item.buttonTextColor ?? COLOR_GREY;
        }
      })
      .on('pointerleave', () => {
        this.mouseout?.();
        if (this.buttonText) {
          this.buttonText.style.fill = item.buttonTextColor ?? COLOR_BLACK;
        }
      });

    this.addChild(buttonContainer).addChild(this.buttonSprite, this.buttonText);
  }

  public async load(item: CTAItem, ratio: number, compressed: boolean): Promise<void> {
    try {
      const { imageHeight, imageURI, imageWidth } = item;

      if (!imageURI) {
        this.logger.error('Invalid imageURI');
        return;
      }

      if (isUndefined(imageWidth) || isUndefined(imageHeight)) {
        this.logger.error('Invalid image dimensions');
        return;
      }

      // TODO: this does not work, image is being loaded twice
      if (this.imageSprite) {
        return;
      }
      const height = imageHeight * ratio;
      const width = imageWidth * ratio;

      // Use compressed format if specified
      const imageUrl = compressed && imageURI.endsWith('.webp') ? imageURI.replace('.webp', '.ktx2') : imageURI;

      this.imageTexture = await Assets.load(imageUrl);
      this.imageSprite = Sprite.from(this.imageTexture);
      this.imageSprite.name = `cta-image-${item.CTAItemId}`;
      this.imageSprite.height = height;
      this.imageSprite.width = width;

      this.imageSprite.anchor.set(0.5, 0.5);
      this.imageSprite.visible = true;
      this.addChild(this.imageSprite);
    } catch {
      this.logger.error('Failed to load image');
    }
  }

  public restoreSprites(): { buttonSprite?: Sprite; imageSprite?: Sprite } {
    return { buttonSprite: this.buttonSprite, imageSprite: this.imageSprite };
  }

  protected buttonSizeExample = new Map<ButtonSize, ButtonSizeInfo>([
    [
      ButtonSize.SMALL,
      {
        buttonSize: ButtonSize.SMALL,
        width: 137,
        height: 42,
        fontSize: 12,
      },
    ],
    [
      ButtonSize.MEDIUM,
      {
        buttonSize: ButtonSize.MEDIUM,
        width: 188,
        height: 49,
        fontSize: 16,
      },
    ],
    [
      ButtonSize.LARGE,
      {
        buttonSize: ButtonSize.LARGE,
        width: 280,
        height: 71,
        fontSize: 30,
      },
    ],
  ]);
}

// Create button
export const createButtonText = (text: string, ratio: number, textColor?: string, fontSize = 20): Text => {
  const size = fontSize * ratio;
  const style = new TextStyle({
    fill: textColor ?? COLOR_BLACK,
    fontFamily: FONT_FAMILY_MEDIUM,
    fontSize: `${size}px`,
    align: 'center',
    padding: 2,
  });

  const formattedText = text;

  const buttonText = new Text(formattedText, style);
  const textWidth = Math.floor(buttonText.width);
  const maxLength = Math.floor(textWidth / size);

  if (textWidth / ratio > BUTTON_WIDTH) {
    let index = maxLength - 1;
    if (maxLength > 16) {
      index = 14;
    }
    const t = text.slice(0, index);
    buttonText.text = t.concat('...');
  }

  buttonText.anchor.set(0.5, 0.5);
  return buttonText;
};

export const generateButtonTexture = (
  renderer: Renderer,
  ratio: number,
  buttonColor?: string,
  customWidth = BUTTON_WIDTH,
  customHeight = BUTTON_HEIGHT,
): Texture => {
  const buttonRadius = 25;
  const height = customHeight * ratio;
  const width = customWidth * ratio;
  const graphics = new Graphics()
    .beginFill(buttonColor ?? COLOR_WHITE)
    .drawRoundedRect(0, 0, width, height, buttonRadius)
    .endFill()
    .lineStyle(1, buttonColor ?? COLOR_WHITE);
  return renderer.generateTexture(graphics);
};

export const generateButtonY = (y: number, imageHeight: number, buttonHeight = BUTTON_HEIGHT): number => {
  return imageHeight / 2 + buttonHeight / 2 + 20;
};

export const generateMapButtonY = (y: number, imageHeight: number, ratio: number): number => {
  return y + (imageHeight * ratio) / 2 + (BUTTON_HEIGHT * ratio) / 2 + 20 * ratio + 2;
};

interface ButtonSizeInfo {
  buttonSize: ButtonSize;
  width: number;
  height: number;
  fontSize: number;
}
